import { useEffect, useState } from "react";

// A scrolling element, or a frame's window when its document scrolls.
type Scroller = HTMLElement | Window;

// Room under the end before it counts as out of view.
const SLACK = 24;

const SCROLL_KEYS = new Set([
  "ArrowUp",
  "ArrowDown",
  "PageUp",
  "PageDown",
  "Home",
  "End",
  " ",
]);

// Typing in the chat's box isn't scrolling.
const typing = ({ target }: Event) => {
  const element = target as HTMLElement | null;
  if (!element?.tagName) return false;
  return (
    element.isContentEditable ||
    ["INPUT", "TEXTAREA", "SELECT"].includes(element.tagName)
  );
};

const isWindow = (scroller: Scroller): scroller is Window =>
  !("getBoundingClientRect" in scroller);

const viewOf = (scroller: Scroller) =>
  isWindow(scroller)
    ? { top: 0, bottom: scroller.innerHeight }
    : scroller.getBoundingClientRect();

// Keeps the end of what's being written in view, until the reader scrolls away
// from it. Only he scrolls with a wheel, a touch, a key or the scrollbar, so
// after one of those it looks, on the next frame, whether the end is still in
// view, and follows only if it is. Our own scrolls and the page growing never
// count. While he holds the scrollbar it doesn't move the pane; a release
// anywhere, a cancel or its window losing focus lets go. `start` follows again,
// for a new section.
export const followTail = (scroller: () => Scroller | null) => {
  let following = true;
  let holding = false;
  let end: Element | null = null;
  let attached: Scroller | null = null;

  const inView = () => {
    if (!end || !attached) return true;

    const { top, bottom } = end.getBoundingClientRect();
    const view = viewOf(attached);
    return bottom <= view.bottom + SLACK && top >= view.top - SLACK;
  };
  const look = () => {
    requestAnimationFrame(() => {
      following = inView();
    });
  };
  const onKey = (event: Event) => {
    if (SCROLL_KEYS.has((event as KeyboardEvent).key) && !typing(event)) look();
  };
  const onHold = () => {
    holding = true;
  };
  const onRelease = () => {
    if (!holding) return;

    holding = false;
    look();
  };

  // A frame's events don't reach the page, so its own window listens, and the
  // page's for a scrollbar let go of outside it.
  const targetsOf = (target: Scroller) => {
    const win = isWindow(target) ? target : target.ownerDocument.defaultView;
    const releases = [...new Set([win, window])].filter((w) => w !== null);
    return { target, win, releases };
  };
  const detach = () => {
    if (!attached) return;

    const { target, win, releases } = targetsOf(attached);
    target.removeEventListener("wheel", look);
    target.removeEventListener("touchmove", look);
    target.removeEventListener("pointerdown", onHold);
    target.removeEventListener("pointercancel", onRelease);
    releases.forEach((w) => w.removeEventListener("pointerup", onRelease));
    win?.removeEventListener("blur", onRelease);
    win?.removeEventListener("keydown", onKey);
    attached = null;
  };
  const attach = () => {
    const next = scroller();
    if (next === attached) return;

    detach();
    if (!next) return;

    const { target, win, releases } = targetsOf(next);
    target.addEventListener("wheel", look, { passive: true });
    target.addEventListener("touchmove", look, { passive: true });
    target.addEventListener("pointerdown", onHold);
    target.addEventListener("pointercancel", onRelease);
    releases.forEach((w) => w.addEventListener("pointerup", onRelease));
    win?.addEventListener("blur", onRelease);
    win?.addEventListener("keydown", onKey);
    attached = next;
  };

  return {
    start: () => {
      following = true;
      holding = false;
    },
    // @param element - the end of what's being written
    follow: (element: Element | null) => {
      attach();
      end = element;
      if (!following || holding || !end || !attached) return;

      const below =
        end.getBoundingClientRect().bottom - viewOf(attached).bottom;
      if (below > 0) attached.scrollBy(0, below);
    },
    detach,
  };
};

// @param scroller - read when following starts, so it can mount later
export const useFollowTail = (scroller: () => Scroller | null) => {
  const [tail] = useState(() => followTail(scroller));
  useEffect(() => tail.detach, [tail]);
  return tail;
};
